import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { GridRowSelectionModel } from "@mui/x-data-grid";

import { EllipsisText } from "../../../ellipsis-text/EllipsisText";
import { formatDecimal } from "../../../format/formatNumber";
import { createColumns } from "../../../table/DataTable.utils";
import { ClaimResponse } from "../../api/ClientFileApi";
import { ClaimsTableWidth, nullsLastNumberComparator } from "../claim-list-tab/ClaimListTab.utils";
import { ClaimStatusKey } from "../claimStatus";
import { ParticipantCell } from "../components/participant-cell/ParticipantCell";
import { useOptionalClaimSelection } from "../context/ClaimSelectionContext";
import { isActiveClaim, sumCurrentSaldo } from "../utils/claimTotals";
import { useClientClaims } from "./useClientClaims";

function matchesSearch(claim: ClaimResponse, search: string): boolean {
  if (!search) {
    return true;
  }
  const lower = search.toLowerCase();
  return (
    claim.creditorName.toLowerCase().includes(lower) ||
    (claim.representativeName?.toLowerCase().includes(lower) ?? false) ||
    (claim.caseNumber?.toLowerCase().includes(lower) ?? false)
  );
}

export function useClaimsList(clientId: string) {
  const { t, i18n } = useTranslation();
  const [searchText, setSearchTextState] = useState("");
  const [showCompleted, setShowCompletedState] = useState(false);
  const [showSrvRegisteredOnly, setShowSrvRegisteredOnlyState] = useState(false);
  const selection = useOptionalClaimSelection();

  const { data: claims = [], isLoading } = useClientClaims(clientId);

  // The SRV filter reads the claim's own field only — a balance entry of the registered type does not
  // make a claim count as registered.
  const visibleClaims = useMemo(
    () =>
      claims.filter(
        (c) =>
          (showCompleted || isActiveClaim(c)) &&
          (!showSrvRegisteredOnly || c.srvRegistrationStatus === ClaimStatusKey.Yes) &&
          matchesSearch(c, searchText)
      ),
    [claims, showCompleted, showSrvRegisteredOnly, searchText]
  );

  const footerSaldo = useMemo(() => sumCurrentSaldo(visibleClaims), [visibleClaims]);
  const footerDisputed = useMemo(() => visibleClaims.reduce((sum, c) => sum + c.disputedAmount, 0), [visibleClaims]);

  // A narrowed list no longer shows what was ticked under the previous one, and the select-all
  // checkbox is meant to tick exactly what is on screen — so a changed filter starts over.
  const clearSelection = selection?.clearSelection;
  const setSearchText = (value: string) => {
    clearSelection?.();
    setSearchTextState(value);
  };
  const setShowCompleted = (value: boolean) => {
    clearSelection?.();
    setShowCompletedState(value);
  };
  const setShowSrvRegisteredOnly = (value: boolean) => {
    clearSelection?.();
    setShowSrvRegisteredOnlyState(value);
  };

  const rowSelectionModel = useMemo<GridRowSelectionModel>(
    () => ({ type: "include", ids: new Set<string>(selection?.selectedClaimIds ?? []) }),
    [selection?.selectedClaimIds]
  );

  // "Select all" arrives as type "exclude" with an empty id set, i.e. everything but nothing. It is
  // resolved against the rows on screen right here, so what is stored is always the explicit set of
  // visible claims the user ticked, and rows the filters hide can never travel with it.
  const onRowSelectionModelChange = (model: GridRowSelectionModel) => {
    const isTicked = (claimId: string) => (model.type === "include" ? model.ids.has(claimId) : !model.ids.has(claimId));

    selection?.setSelectedClaimIds(new Set(visibleClaims.filter((c) => isTicked(c.id)).map((c) => c.id)));
  };

  const format = (amount: number) => formatDecimal(amount, i18n.language);

  const columns = createColumns<ClaimResponse>([
    {
      field: "creditorName",
      headerName: t("claims.table.creditor"),
      flex: 1,
      renderCell: (params) => {
        const row = params.row as ClaimResponse;
        return (
          <ParticipantCell
            name={row.creditorName}
            address={row.creditorAddress ?? null}
            isInactive={row.isCreditorInactive}
            inactiveTooltip={t("claims.table.inactiveCreditor")}
            muted={row.isCompleted}
          />
        );
      },
    },
    {
      field: "representativeName",
      headerName: t("claims.table.representative"),
      flex: 1,
      renderCell: (params) => {
        const row = params.row as ClaimResponse;
        return (
          <ParticipantCell
            name={row.representativeName ?? null}
            address={row.representativeAddress ?? null}
            isInactive={row.isRepresentativeInactive === true}
            inactiveTooltip={t("claims.table.inactiveRepresentative")}
            muted={row.isCompleted}
          />
        );
      },
    },
    {
      field: "caseNumber",
      headerName: t("claims.table.caseNumber"),
      flex: 1,
      maxWidth: ClaimsTableWidth.caseNumber,
      renderCell: (params) => (
        <EllipsisText
          value={params.value as string | null}
          variant="body2"
          color={(params.row as ClaimResponse).isCompleted ? "text.disabled" : undefined}
        />
      ),
    },
    {
      field: "legalBasis",
      headerName: t("claims.table.legalBasis"),
      flex: 1,
      maxWidth: ClaimsTableWidth.legalBasis,
      renderCell: (params) => (
        <EllipsisText
          value={params.value as string}
          variant="body2"
          color={(params.row as ClaimResponse).isCompleted ? "text.disabled" : undefined}
        />
      ),
    },
    {
      field: "srvOrderNumber",
      headerName: t("claims.table.srvOrderNumber"),
      type: "number",
      flex: 1,
      maxWidth: ClaimsTableWidth.srvOrderNumber,
      align: "left",
      headerAlign: "left",
      getSortComparator: nullsLastNumberComparator,
      renderCell: (params) => (
        <EllipsisText
          value={params.value == null ? null : String(params.value as number)}
          variant="body2"
          color={(params.row as ClaimResponse).isCompleted ? "text.disabled" : undefined}
        />
      ),
    },
    {
      field: "currentSaldo",
      headerName: t("claims.table.currentSaldo"),
      flex: 1,
      maxWidth: ClaimsTableWidth.currentSaldo,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => (
        <EllipsisText
          value={`${format(params.value as number)} €`}
          variant="body2"
          color={(params.row as ClaimResponse).isCompleted ? "text.disabled" : undefined}
        />
      ),
    },
    {
      field: "disputedAmount",
      headerName: t("claims.table.disputedAmount"),
      flex: 1,
      maxWidth: ClaimsTableWidth.disputedAmount,
      align: "right",
      headerAlign: "right",
      renderCell: (params) => {
        const amount = params.value as number;
        const muted = (params.row as ClaimResponse).isCompleted;
        return (
          <EllipsisText
            value={`${format(amount)} €`}
            variant="body2"
            color={muted ? "text.disabled" : amount > 0 ? "warning.main" : undefined}
          />
        );
      },
    },
  ]);

  return {
    claims: visibleClaims,
    columns,
    isLoading,
    footerSaldo,
    footerDisputed,
    searchText,
    setSearchText,
    showCompleted,
    setShowCompleted,
    showSrvRegisteredOnly,
    setShowSrvRegisteredOnly,
    /** Rows carry a selection checkbox only where a selection is held, i.e. not in the portal. */
    isSelectable: selection !== null,
    rowSelectionModel,
    onRowSelectionModelChange,
  };
}
